import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { uploadSmart } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FileChips, UploadProgress, dropProps, statusesForZone, hubsForZone, useSaleStatuses } from "@/components/shared";
import { toast } from "sonner";
import { Save, Paperclip, Upload, Download } from "lucide-react";

const ZONES = ["Blue Zone", "Green Zone", "Ofisler"];
const TYPES = ["Fuar Standı", "Etkinlik", "Teknik"];

export default function ProjectCreatePage() {
  const [name, setName] = useState("");
  const [zone, setZone] = useState("");
  const [pstatus, setPstatus] = useState("");
  const [hub, setHub] = useState("");
  const [saleStatus, setSaleStatus] = useState("");
  const saleStatuses = useSaleStatuses();
  const [hubs, setHubs] = useState([]);
  const [standNo, setStandNo] = useState("");
  const [ptype, setPtype] = useState("");
  const [standType, setStandType] = useState("");
  const [areaM2, setAreaM2] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInput = useRef(null);
  const importInput = useRef(null);
  const navigate = useNavigate();

  const onFiles = async (e) => {
    const list = Array.from(e?.target?.files || e || []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      const data = await uploadSmart(list, setProgress);
      setFiles((f) => [...f, ...data]);
    } catch (err) {
      toast.error(err?.message || apiError(err));
    } finally {
      setUploading(false);
      setProgress(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/projects/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "proje_sablon.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Şablon indirilemedi");
    }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImportBusy(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/projects/import", fd);
      setImportResult(data);
      if (data.created > 0) toast.success(`${data.created} proje eklendi`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImportBusy(false);
      if (importInput.current) importInput.current.value = "";
    }
  };

  const [nameCheck, setNameCheck] = useState(null);
  useEffect(() => {
    api.get("/settings/hubs").then((r) => setHubs(r.data.hubs || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const q = name.trim();
    if (q.length < 2) { setNameCheck(null); return; }
    const t = setTimeout(() => {
      api.get(`/projects/check-name?name=${encodeURIComponent(q)}`)
        .then((r) => setNameCheck(r.data))
        .catch(() => setNameCheck(null));
    }, 350);
    return () => clearTimeout(t);
  }, [name]);

  const submit = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (!name.trim()) e.name = "Proje adı zorunludur";
    if (nameCheck?.exact) e.name = "Bu isimle bir proje zaten mevcut. Aynı isimle proje oluşturulamaz.";
    if (!zone) e.zone = "Bölge seçin";
    if (!standNo.trim()) e.standNo = "Stand no zorunludur";
    if (!ptype) e.ptype = "Tür seçin";
    if (!areaM2.trim()) e.areaM2 = "Alan (m²) zorunludur";
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    try {
      await api.post("/projects", { name, zone, stand_no: standNo, project_type: ptype, stand_type: ptype === "Fuar Standı" ? (standType || null) : null, area_m2: areaM2, note, file_ids: files.map((f) => f.id), project_status: pstatus || null, hub: hub || null, sale_status: saleStatus || null });
      toast.success("Proje oluşturuldu");
      navigate("/admin/projeler");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 fade-in max-w-xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Projeler</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Proje Oluştur</h1>
        </div>
        <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="project-import-button" className="rounded-none border-slate-200 hover:bg-slate-100 gap-2 transition-colors duration-200">
              <Upload size={15} /> Toplu İçe Aktar
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-bold tracking-tight">Toplu Proje İçe Aktar</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Excel (.xlsx) veya CSV dosyasıyla birden fazla projeyi tek seferde ekleyin.
                Sütunlar: <span className="font-medium text-slate-800">Proje Adı, Bölge, Stand No, Tür, Not</span>.
              </p>
              <Button variant="outline" onClick={downloadTemplate} data-testid="project-template-download" className="rounded-none border-slate-200 hover:bg-slate-100 gap-2 w-full transition-colors duration-200">
                <Download size={15} /> Örnek Şablonu İndir
              </Button>
              <input ref={importInput} type="file" accept=".xlsx,.csv" className="hidden" onChange={onImportFile} data-testid="project-import-file-input" />
              <Button onClick={() => importInput.current?.click()} disabled={importBusy} data-testid="project-import-upload" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 w-full font-semibold transition-colors duration-200">
                <Upload size={15} /> {importBusy ? "İçe aktarılıyor..." : "Dosya Seç ve İçe Aktar"}
              </Button>
              <p className="text-xs text-slate-500">Bölge: Blue Zone / Green Zone / Ofisler · Tür: Fuar Standı / Etkinlik / Teknik. Not sütunu opsiyoneldir.</p>
              {importResult && (
                <div className="border border-slate-200 p-4 space-y-2 max-h-56 overflow-y-auto" data-testid="project-import-result">
                  {importResult.created > 0 && (
                    <p className="text-sm text-emerald-600 font-medium">{importResult.created} proje başarıyla eklendi.</p>
                  )}
                  {importResult.errors?.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-red-600">{importResult.errors.length} satır eklenemedi:</p>
                      {importResult.errors.map((er, i) => (
                        <p key={i} className="text-xs text-slate-600">Satır {er.row}: {er.error}</p>
                      ))}
                    </div>
                  )}
                  {importResult.created > 0 && (
                    <Button variant="outline" size="sm" onClick={() => navigate("/admin/projeler")} className="rounded-none border-slate-200 hover:bg-slate-100 mt-1">
                      Projelere Git
                    </Button>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="pname">Proje Adı</Label>
          <Input id="pname" data-testid="project-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="örn. Suudi Arabistan Standı" className={`rounded-none bg-white h-11 ${nameCheck?.exact ? "border-red-400" : nameCheck?.similar?.length ? "border-amber-400" : "border-slate-200"}`} />
          {nameCheck?.similar?.length > 0 && (
            <div className={`border px-3 py-2.5 text-xs space-y-1 ${nameCheck.exact ? "border-red-300 bg-red-50 text-red-700" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="project-name-warning">
              <div className="font-semibold">
                {nameCheck.exact
                  ? "Bu isimle bir proje zaten mevcut — aynı isimle proje oluşturulamaz."
                  : "Dikkat: Bu projeye benzer bir isimle proje/ihale mevcut."}
              </div>
              {nameCheck.similar.map((s) => (
                <div key={s.id} className="flex items-center gap-1.5" data-testid={`similar-project-${s.id}`}>
                  <span className="truncate">• {s.name}</span>
                  {s.has_tender && <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold border border-red-200 bg-white text-red-500 px-1 py-0.5">İhale Açılmış</span>}
                  {s.exact && <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold bg-red-500 text-white px-1 py-0.5">Aynı İsim</span>}
                </div>
              ))}
            </div>
          )}
          {errors.name && <p className="text-red-500 text-sm" data-testid="project-name-error">{errors.name}</p>}
        </div>

        <div className="space-y-1.5">
          <Label>Bölge</Label>
          <Select value={zone} onValueChange={(v) => { if (v) { setZone(v); setPstatus((cur) => (statusesForZone(v).includes(cur) ? cur : "")); setHub((cur) => (hubsForZone(hubs, v).includes(cur) ? cur : "")); } }}>
            <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="project-zone-select">
              <SelectValue placeholder="Bölge seçin">{zone}</SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200 rounded-none">
              {ZONES.map((z) => (
                <SelectItem key={z} value={z} data-testid={`zone-option-${z.replace(/\s/g, "-")}`}>{z}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.zone && <p className="text-red-500 text-sm" data-testid="project-zone-error">{errors.zone}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="standno">Stand No</Label>
          <Input id="standno" data-testid="project-standno-input" value={standNo} onChange={(e) => setStandNo(e.target.value)} placeholder="örn. B-24" className="rounded-none bg-white border-slate-200 h-11" />
          {errors.standNo && <p className="text-red-500 text-sm" data-testid="project-standno-error">{errors.standNo}</p>}
        </div>

        <div className="space-y-1.5">
          <Label>Tür</Label>
          <Select value={ptype} onValueChange={(v) => { if (v) { setPtype(v); if (v !== "Fuar Standı") setStandType(""); } }}>
            <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="project-type-select">
              <SelectValue placeholder="Tür seçin">{ptype}</SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200 rounded-none">
              {TYPES.map((t) => (
                <SelectItem key={t} value={t} data-testid={`type-option-${t.replace(/\s/g, "-")}`}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.ptype && <p className="text-red-500 text-sm" data-testid="project-type-error">{errors.ptype}</p>}
        </div>

        {ptype === "Fuar Standı" && (
          <div className="space-y-1.5">
            <Label>Stand Türü <span className="text-slate-400 font-normal">(opsiyonel)</span></Label>
            <Select value={standType || "__none__"} onValueChange={(v) => setStandType(v === "__none__" ? "" : v)}>
              <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="project-stand-type-select">
                <SelectValue placeholder="Atanmamış">{standType || "Atanmamış"}</SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 rounded-none">
                <SelectItem value="__none__" data-testid="stand-type-option-none">Atanmamış</SelectItem>
                {["Custom", "Template", "Customized Template"].map((t) => (
                  <SelectItem key={t} value={t} data-testid={`stand-type-option-${t.replace(/\s/g, "-")}`}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Statü <span className="text-slate-400 font-normal">(opsiyonel)</span></Label>
          <Select value={pstatus} onValueChange={(v) => { if (v) setPstatus(v); }}>
            <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="project-status-select">
              <SelectValue placeholder={zone === "Blue Zone" ? "Custom / Customised Template / Template" : "Özel / SBB seçin"}>{pstatus}</SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200 rounded-none">
              {statusesForZone(zone).map((st) => (
                <SelectItem key={st} value={st} data-testid={`project-status-option-${st}`}>{st}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>Hub <span className="text-slate-400 font-normal">(opsiyonel)</span></Label>
          <Select value={hub} onValueChange={(v) => { if (v) setHub(v); }}>
            <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="project-hub-select">
              <SelectValue placeholder="Hub seçin">{hub}</SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200 rounded-none">
              {hubsForZone(hubs, zone).map((h) => (
                <SelectItem key={h} value={h} data-testid={`project-hub-option-${h}`}>{h}</SelectItem>
              ))}
              {hubsForZone(hubs, zone).length === 0 && <div className="py-2 px-3 text-sm text-slate-400">Bu bölge için tanımlı hub yok</div>}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>Durum <span className="text-slate-400 font-normal">(opsiyonel)</span></Label>
          <Select value={saleStatus} onValueChange={(v) => { if (v) setSaleStatus(v); }}>
            <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="project-sale-status-select">
              <SelectValue placeholder="Seçin">{saleStatus}</SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200 rounded-none">
              {saleStatuses.map((s) => <SelectItem key={s} value={s} data-testid={`sale-status-option-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="area">Alan (m²)</Label>
          <Input id="area" data-testid="project-area-input" value={areaM2} onChange={(e) => setAreaM2(e.target.value)} placeholder="örn. 400" inputMode="numeric" className="rounded-none bg-white border-slate-200 h-11" />
          {errors.areaM2 && <p className="text-red-500 text-sm" data-testid="project-area-error">{errors.areaM2}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pnote">Not <span className="text-slate-400 font-normal">(serbest metin, opsiyonel)</span></Label>
          <Textarea id="pnote" data-testid="project-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Proje ile ilgili iç notlarınız, hatırlatmalar, özel gereksinimler..." className="rounded-none bg-white border-slate-200 text-sm" />
        </div>

        <div className="space-y-1.5">
          <Label>Proje Dosyaları</Label>
          <input ref={fileInput} type="file" multiple className="hidden" onChange={onFiles} data-testid="project-file-input" />
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            {...dropProps(onFiles, setDragOver)}
            data-testid="project-file-dropzone"
            className={`w-full border border-dashed transition-colors duration-200 p-6 flex flex-col items-center gap-2 ${dragOver ? "border-slate-900 bg-slate-100 text-slate-900" : "border-slate-300 bg-white hover:border-slate-400 text-slate-500"}`}
          >
            <Paperclip size={18} />
            <span className="text-sm">{uploading ? "Yükleniyor..." : dragOver ? "Dosyaları buraya bırakın" : "Dosya seçmek için tıklayın veya sürükleyip bırakın"}</span>
          </button>
          {uploading && <UploadProgress progress={progress} />}
          {files.length > 0 && (
            <div className="pt-2">
              <FileChips files={files} onRemove={(id) => setFiles((arr) => arr.filter((x) => x.id !== id))} />
            </div>
          )}
          <p className="text-xs text-slate-500">Dosya başına 3 GB'a kadar yükleyebilirsiniz. Bu dosyalar, teklif alırken proje seçildiğinde talebe otomatik eklenir.</p>
        </div>

        <Button type="submit" disabled={busy || uploading} data-testid="project-save-button" className="rounded-none h-12 px-8 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
          <Save size={15} /> {busy ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </form>
    </div>
  );
}
